import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ComponentType,
} from "react";
import { toast } from "sonner";
import {
  ChevronLeft,
  Blocks,
  Building2,
  CheckCircle2,
  CircleAlert,
  UserRound,
  HardDrive,
  KeyRound,
  Loader2,
  Mic,
  Monitor,
  RefreshCw,
  Send,
  ShieldCheck,
  Trash2,
  Upload,
  Users,
  Webhook,
} from "lucide-react";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/ui/avatar";
import { ApiKeysSection } from "@/components/settings/ApiKeysSection";
import { WebhooksSection } from "@/components/settings/WebhooksSection";
import { DevicesSection } from "@/components/settings/DevicesSection";
import { IntegrationsSection } from "@/components/settings/IntegrationsSection";
import { PreviewsSection } from "@/components/settings/PreviewsSection";
import { PrivacySection } from "@/components/settings/PrivacySection";
import { VoiceTestModal } from "@/components/spoken-memory/VoiceTestModal";
import {
  createSpokenMemoryApi,
  enrollmentRedoCopy,
  useMyVoiceProfileQuery,
  useRetrySpeakerSetupMutation,
  type SpokenMemoryApi,
  type VoiceProfile,
} from "@/data/spoken-memory";
import { useSession } from "@/data/auth";
import {
  useUpdateMyProfileMutation,
  useUploadMyAvatarMutation,
} from "@/data/profile";
import { initialsFromName } from "@/utils";
import { env } from "@/env";
import {
  useCreateInviteMutation,
  useCurrentWorkspace,
  useRemoveMemberMutation,
  useRevokeInviteMutation,
  useUpdateMemberRoleMutation,
  useUpdateWorkspaceNameMutation,
  useUploadWorkspaceLogoMutation,
  useWorkspaceInvitesQuery,
  useWorkspaceMembersQuery,
  useWorkspaceQuery,
  type WorkspaceInvitationRow,
  type WorkspaceMemberRow,
  type WorkspaceRoleSlug,
  type WorkspaceSummary,
} from "@/data/workspaces";
import {
  FormCard,
  FormRow,
  FormStackedRow,
  InlineMessage,
  SettingsHeader,
} from "@/components/settings/settings-ui";

function absoluteUrl(url: string | null): string | null {
  if (!url) return null;
  if (/^https?:/i.test(url)) return url;
  return `${env.API_BASE_URL}${url}`;
}

export type SettingsTab =
  | "workspace"
  | "members"
  | "profile"
  | "devices"
  | "voice-profiles"
  | "integrations"
  | "previews"
  | "privacy"
  | "api"
  | "webhooks";

interface SettingsScreenProps {
  tab: SettingsTab;
  onBack: () => void;
  onTabChange: (tab: SettingsTab) => void;
}

type IconComponent = ComponentType<{ className?: string }>;

const NAV_ITEMS: Array<{
  id: SettingsTab;
  label: string;
  icon: IconComponent;
}> = [
  { id: "workspace", label: "Workspace", icon: Building2 },
  { id: "members", label: "Members", icon: Users },
  { id: "profile", label: "Profile", icon: UserRound },
  { id: "devices", label: "Devices", icon: HardDrive },
  { id: "voice-profiles", label: "Voice profiles", icon: Mic },
  { id: "integrations", label: "Integrations", icon: Blocks },
  { id: "previews", label: "Previews", icon: Monitor },
  { id: "privacy", label: "Privacy", icon: ShieldCheck },
  { id: "api", label: "API", icon: KeyRound },
  { id: "webhooks", label: "Webhooks", icon: Webhook },
];

export function SettingsScreen({
  tab,
  onBack,
  onTabChange,
}: SettingsScreenProps) {
  const { currentSlug, currentMembership } = useCurrentWorkspace();

  return (
    <>
      <aside className="flex w-[248px] shrink-0 flex-col gap-1 border-r border-border bg-sidebar p-3">
        <button
          type="button"
          onClick={onBack}
          className="mb-2 flex items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-[13px] text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Back to app
        </button>

        {NAV_ITEMS.map((item) => renderNavButton(item, tab, onTabChange))}

        <div className="flex-1" aria-hidden />
      </aside>

      <main className="min-w-0 flex-1 overflow-auto bg-background p-9">
        {tab === "workspace" ? (
          <WorkspaceTab />
        ) : tab === "members" ? (
          <MembersTab />
        ) : tab === "profile" ? (
          <ProfileTab />
        ) : tab === "devices" ? (
          <DevicesSection workspaceSlug={currentSlug} />
        ) : tab === "integrations" ? (
          <IntegrationsSection
            workspaceSlug={currentSlug}
            canManage={currentMembership?.role === "admin"}
          />
        ) : tab === "previews" ? (
          <PreviewsSection
            workspaceSlug={currentSlug}
            canManage={currentMembership?.role === "admin"}
          />
        ) : tab === "privacy" ? (
          <PrivacySection
            workspaceSlug={currentSlug}
            canManage={currentMembership?.role === "admin"}
          />
        ) : tab === "api" ? (
          <ApiKeysSection
            workspaceSlug={currentSlug}
            canManage={currentMembership?.role === "admin"}
          />
        ) : tab === "webhooks" ? (
          <WebhooksSection
            workspaceSlug={currentSlug}
            canManage={currentMembership?.role === "admin"}
          />
        ) : (
          <VoiceProfilesContainer />
        )}
      </main>
    </>
  );
}

export function normalizeSettingsTab(tab: unknown): SettingsTab {
  return tab === "members" ||
    tab === "profile" ||
    tab === "devices" ||
    tab === "voice-profiles" ||
    tab === "integrations" ||
    tab === "previews" ||
    tab === "privacy" ||
    tab === "api" ||
    tab === "webhooks"
    ? tab
    : "workspace";
}

function renderNavButton(
  item: (typeof NAV_ITEMS)[number],
  tab: SettingsTab,
  setTab: (next: SettingsTab) => void,
) {
  const Icon = item.icon;
  const active = tab === item.id;
  return (
    <button
      type="button"
      key={item.id}
      onClick={(e) => {
        setTab(item.id);
        e.currentTarget.blur();
      }}
      className={`flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-[13px] transition-colors outline-none focus-visible:outline-none ${
        active
          ? "bg-card font-medium text-foreground"
          : "text-muted-foreground hover:bg-background"
      }`}
    >
      <Icon className={`h-3.5 w-3.5 ${active ? "opacity-90" : "opacity-70"}`} />
      {item.label}
    </button>
  );
}

function WorkspaceTab() {
  const { currentSlug, currentWorkspace } = useCurrentWorkspace();
  const workspaceQuery = useWorkspaceQuery(currentSlug);
  const updateName = useUpdateWorkspaceNameMutation(currentSlug);
  const uploadLogo = useUploadWorkspaceLogoMutation(currentSlug);
  const [workspace, setWorkspace] = useState<WorkspaceSummary | null>(
    currentWorkspace ?? null,
  );
  const [name, setName] = useState(currentWorkspace?.name ?? "");
  const lastSavedName = useRef(currentWorkspace?.name ?? "");
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const next = workspaceQuery.data?.workspace ?? currentWorkspace ?? null;
    if (!next) return;
    setWorkspace(next);
    setName(next.name);
    lastSavedName.current = next.name;
  }, [currentWorkspace, workspaceQuery.data?.workspace]);

  const saveName = async () => {
    if (!currentSlug) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === lastSavedName.current) return;
    try {
      const data = await updateName.mutateAsync(trimmed);
      setWorkspace(data.workspace);
      setName(data.workspace.name);
      lastSavedName.current = data.workspace.name;
      toast.success("Workspace updated");
    } catch {
      toast.error("Failed to update workspace");
      setName(lastSavedName.current);
    }
  };

  const onPickLogo = () => fileRef.current?.click();

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!currentSlug) return;
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick a PNG, JPEG, WebP, or GIF image");
      return;
    }
    try {
      const data = await uploadLogo.mutateAsync(file);
      setWorkspace(data.workspace);
      toast.success("Workspace updated");
    } catch {
      toast.error("Failed to upload logo");
    }
  };

  const logoSrc = absoluteUrl(workspace?.logo_url ?? null);
  const initial = (name.trim()[0] ?? "P").toUpperCase();

  return (
    <div className="mx-auto max-w-3xl">
      <h2 className="mb-5 text-[20px] font-semibold tracking-tight">
        Workspace
      </h2>

      <FormCard>
        <FormRow label="Logo" hint="Recommended size is 256x256px">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={onFileChange}
          />
          <button
            type="button"
            onClick={onPickLogo}
            className="group relative cursor-pointer overflow-hidden rounded-md"
            aria-label="Upload workspace logo"
          >
            <Avatar className="h-9 w-9 rounded-md">
              {logoSrc ? <AvatarImage src={logoSrc} alt="" /> : null}
              <AvatarFallback className="rounded-md bg-[oklch(0.78_0.16_160)] text-[13px] font-semibold text-background">
                {initial}
              </AvatarFallback>
            </Avatar>
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md bg-black/0 opacity-0 transition-colors duration-150 group-hover:bg-black/45 group-hover:opacity-100">
              <Upload className="h-4 w-4 text-white" />
            </span>
          </button>
        </FormRow>
        <FormRow label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => void saveName()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                (e.target as HTMLInputElement).blur();
              }
            }}
            className="w-full"
          />
        </FormRow>
      </FormCard>
    </div>
  );
}

export function inviteRoleChoicesFor(
  role: string | null | undefined,
): WorkspaceRoleSlug[] {
  if (role === "admin") return ["admin", "member"];
  return ["member"];
}

type MemberRosterRow =
  | { kind: "member"; id: string; member: WorkspaceMemberRow }
  | {
      kind: "invite";
      id: string;
      email: string;
      role: WorkspaceRoleSlug;
      created_at: string | number;
      invitation: WorkspaceInvitationRow | null;
      member: WorkspaceMemberRow | null;
    };

export function buildMemberRosterRows(
  members: WorkspaceMemberRow[],
  invitations: WorkspaceInvitationRow[],
): MemberRosterRow[] {
  const rows: MemberRosterRow[] = [];
  const seenEmails = new Set<string>();
  const pendingInvitations = invitations.filter(
    (invitation) => invitation.state === "pending",
  );
  const invitationByEmail = new Map(
    pendingInvitations.map((invitation) => [
      invitation.email.trim().toLowerCase(),
      invitation,
    ]),
  );

  for (const member of members) {
    const emailKey = member.user_email.trim().toLowerCase();
    if (seenEmails.has(emailKey)) continue;
    seenEmails.add(emailKey);
    if (member.status === "pending") {
      const invitation = invitationByEmail.get(emailKey) ?? null;
      rows.push({
        kind: "invite",
        id: `pending-member:${member.id}`,
        email: member.user_email,
        role: member.role,
        created_at: invitation?.created_at ?? member.created_at,
        invitation,
        member,
      });
      continue;
    }
    if (member.status === "active") {
      rows.push({ kind: "member", id: `member:${member.id}`, member });
    }
  }

  for (const invitation of pendingInvitations) {
    const emailKey = invitation.email.trim().toLowerCase();
    if (seenEmails.has(emailKey)) continue;
    seenEmails.add(emailKey);
    rows.push({
      kind: "invite",
      id: `invitation:${invitation.id}`,
      email: invitation.email,
      role: invitation.role,
      created_at: invitation.created_at,
      invitation,
      member: null,
    });
  }

  return rows;
}

function MembersTab() {
  const { currentSlug, currentWorkspace, currentMembership } =
    useCurrentWorkspace();
  const membersQuery = useWorkspaceMembersQuery(currentWorkspace?.id);
  const invitesQuery = useWorkspaceInvitesQuery(currentSlug);
  const createInvite = useCreateInviteMutation(currentSlug);
  const updateMemberRole = useUpdateMemberRoleMutation(
    currentWorkspace?.id ?? null,
  );
  const removeMemberMutation = useRemoveMemberMutation(
    currentWorkspace?.id ?? null,
  );
  const revokeInviteMutation = useRevokeInviteMutation(currentSlug);
  const [email, setEmail] = useState("");
  // Only admins choose what role to invite as; members always invite as "member".
  const canChooseInviteRole = currentMembership?.role === "admin";
  const roleChoices = inviteRoleChoicesFor(currentMembership?.role);
  const [inviteRole, setInviteRole] = useState<WorkspaceRoleSlug>(
    roleChoices[0] ?? "member",
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const members = membersQuery.data?.members;
  const invitations = invitesQuery.data?.invitations;
  const rosterRows = useMemo(
    () => buildMemberRosterRows(members ?? [], invitations ?? []),
    [members, invitations],
  );
  const loading = membersQuery.isPending || invitesQuery.isPending;

  useEffect(() => {
    if (!roleChoices.includes(inviteRole)) {
      setInviteRole(roleChoices[0] ?? "member");
    }
  }, [inviteRole, roleChoices]);

  async function sendInvite() {
    if (!currentSlug || !email.trim()) return;
    setBusyId("invite");
    setError(null);
    try {
      await createInvite.mutateAsync({
        email: email.trim(),
        role: inviteRole,
      });
      await Promise.all([membersQuery.refetch(), invitesQuery.refetch()]);
      setEmail("");
      toast.success("Invitation sent");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast.error("Failed to send invitation");
    } finally {
      setBusyId(null);
    }
  }

  async function updateRole(
    member: WorkspaceMemberRow,
    role: WorkspaceRoleSlug,
  ) {
    if (!currentWorkspace?.id || role === member.role) return;
    setBusyId(member.id);
    setError(null);
    try {
      await updateMemberRole.mutateAsync({ userId: member.user_id, role });
      toast.success("Member updated");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast.error("Failed to update member");
    } finally {
      setBusyId(null);
    }
  }

  async function removeMember(member: WorkspaceMemberRow) {
    if (!currentWorkspace?.id) return;
    setBusyId(member.id);
    setError(null);
    try {
      await removeMemberMutation.mutateAsync(member.user_id);
      await membersQuery.refetch();
      toast.success("Member removed");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast.error("Failed to remove member");
    } finally {
      setBusyId(null);
    }
  }

  async function revokeInvite(invitation: WorkspaceInvitationRow) {
    setBusyId(invitation.id);
    setError(null);
    try {
      await revokeInviteMutation.mutateAsync(invitation.workos_invitation_id);
      await Promise.all([membersQuery.refetch(), invitesQuery.refetch()]);
      toast.success("Invitation revoked");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast.error("Failed to revoke invitation");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <SettingsHeader
        title="Members"
        description="Manage workspace access and pending invitations."
      />

      <div className="space-y-6">
        {error ? <InlineMessage variant="error">{error}</InlineMessage> : null}

        <FormCard>
          <FormStackedRow label="Invite">
            <div
              className={`grid gap-2 ${
                canChooseInviteRole
                  ? "md:grid-cols-[minmax(0,1fr)_140px_auto]"
                  : "md:grid-cols-[minmax(0,1fr)_auto]"
              }`}
            >
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void sendInvite();
                  }
                }}
                placeholder="email@example.com"
              />
              {canChooseInviteRole ? (
                <Select
                  value={inviteRole}
                  onValueChange={(value) =>
                    setInviteRole(value as WorkspaceRoleSlug)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roleChoices.map((role) => (
                      <SelectItem key={role} value={role}>
                        {roleLabel(role)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
              <Button
                type="button"
                disabled={busyId === "invite" || !email.trim()}
                onClick={() => void sendInvite()}
              >
                {busyId === "invite" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
                Invite
              </Button>
            </div>
          </FormStackedRow>
        </FormCard>

        <FormCard>
          <FormStackedRow label="Roster">
            {loading ? (
              <div className="flex items-center gap-2 py-3 text-[13px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading members
              </div>
            ) : rosterRows.length === 0 ? (
              <EmptyProcessingRow text="No members or invitations." />
            ) : (
              <div className="divide-y divide-border border-t border-border">
                {rosterRows.map((row) =>
                  row.kind === "member" ? (
                    <MemberRosterActiveRow
                      key={row.id}
                      member={row.member}
                      busyId={busyId}
                      onUpdateRole={updateRole}
                      onRemove={removeMember}
                    />
                  ) : (
                    <MemberRosterInviteRow
                      key={row.id}
                      row={row}
                      busyId={busyId}
                      onRevoke={revokeInvite}
                    />
                  ),
                )}
              </div>
            )}
          </FormStackedRow>
        </FormCard>
      </div>
    </div>
  );
}

function MemberRosterActiveRow({
  member,
  busyId,
  onUpdateRole,
  onRemove,
}: {
  member: WorkspaceMemberRow;
  busyId: string | null;
  onUpdateRole: (
    member: WorkspaceMemberRow,
    role: WorkspaceRoleSlug,
  ) => Promise<void>;
  onRemove: (member: WorkspaceMemberRow) => Promise<void>;
}) {
  return (
    <div className="grid items-center gap-3 py-3 md:grid-cols-[minmax(0,1fr)_150px_96px]">
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <div className="truncate text-[13px] font-medium text-foreground">
            {member.user_name ?? member.user_email}
          </div>
          <MemberStateBadge state="active" />
        </div>
        <div className="truncate text-[12px] text-muted-foreground">
          {member.user_email} · joined {formatSettingsDate(member.created_at)}
        </div>
      </div>
      {member.assignable_roles.length > 0 ? (
        <Select
          value={member.role}
          disabled={busyId === member.id}
          onValueChange={(value) =>
            void onUpdateRole(member, value as WorkspaceRoleSlug)
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {member.assignable_roles.map((role) => (
              <SelectItem key={role} value={role}>
                {roleLabel(role)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <div className="flex h-9 items-center px-3 text-[13px] text-muted-foreground">
          {roleLabel(member.role)}
        </div>
      )}
      {member.can_remove ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busyId === member.id}
          onClick={() => void onRemove(member)}
        >
          {busyId === member.id ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" />
          )}
          Remove
        </Button>
      ) : (
        <div />
      )}
    </div>
  );
}

function MemberRosterInviteRow({
  row,
  busyId,
  onRevoke,
}: {
  row: Extract<MemberRosterRow, { kind: "invite" }>;
  busyId: string | null;
  onRevoke: (invitation: WorkspaceInvitationRow) => Promise<void>;
}) {
  const invitation = row.invitation;
  return (
    <div className="grid items-center gap-3 py-3 md:grid-cols-[minmax(0,1fr)_150px_96px]">
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <div className="truncate text-[13px] font-medium text-foreground">
            {row.member?.user_name ?? row.email}
          </div>
          <MemberStateBadge state="invited" />
        </div>
        <div className="truncate text-[12px] text-muted-foreground">
          {row.email} · invited {formatSettingsDate(row.created_at)}
        </div>
      </div>
      <div className="flex h-9 items-center px-3 text-[13px] text-muted-foreground">
        {roleLabel(row.role)}
      </div>
      {invitation ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busyId === invitation.id}
          onClick={() => void onRevoke(invitation)}
        >
          {busyId === invitation.id ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" />
          )}
          Revoke
        </Button>
      ) : (
        <div />
      )}
    </div>
  );
}

function MemberStateBadge({ state }: { state: "active" | "invited" }) {
  const className =
    state === "active"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
      : "border-amber-500/30 bg-amber-500/10 text-amber-200";
  return (
    <span
      className={`rounded-full border px-2 py-0.5 text-[11px] ${className}`}
    >
      {state === "active" ? "Active" : "Invited"}
    </span>
  );
}

function ProfileTab() {
  const session = useSession();
  const updateProfile = useUpdateMyProfileMutation();
  const uploadAvatar = useUploadMyAvatarMutation();
  const user = session.user;
  const initialFullName = user
    ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
    : "";

  const [fullName, setFullName] = useState(initialFullName);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(
    user?.profilePictureUrl ?? null,
  );
  const lastSavedFullName = useRef(initialFullName);
  const fileRef = useRef<HTMLInputElement>(null);

  // The session.user refresh may arrive after first render — sync once.
  useEffect(() => {
    if (!user) return;
    const next = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
    if (lastSavedFullName.current === "") {
      setFullName(next);
      lastSavedFullName.current = next;
    }
    if (avatarUrl === null && user.profilePictureUrl) {
      setAvatarUrl(user.profilePictureUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const saveName = async () => {
    const trimmed = fullName.trim();
    if (trimmed === lastSavedFullName.current) return;
    try {
      await updateProfile.mutateAsync({ fullName: trimmed });
      lastSavedFullName.current = trimmed;
      toast.success("Profile updated");
    } catch {
      toast.error("Failed to update profile");
      setFullName(lastSavedFullName.current);
    }
  };

  const onPickAvatar = () => fileRef.current?.click();

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick a PNG, JPEG, WebP, or GIF image");
      return;
    }
    try {
      const data = await uploadAvatar.mutateAsync(file);
      setAvatarUrl(data.url);
      toast.success("Profile updated");
    } catch {
      toast.error("Failed to upload picture");
    }
  };

  const initials = initialsFromName(
    fullName || user?.email?.split("@")[0] || "?",
  );
  const email = user?.email ?? "—";
  const avatarSrc = absoluteUrl(avatarUrl);

  return (
    <div className="mx-auto max-w-3xl">
      <h2 className="mb-5 text-[20px] font-semibold tracking-tight">Profile</h2>

      <FormCard>
        <FormRow label="Profile picture">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={onFileChange}
          />
          <button
            type="button"
            onClick={onPickAvatar}
            className="group relative cursor-pointer overflow-hidden rounded-full"
            aria-label="Upload profile picture"
          >
            <Avatar className="h-9 w-9">
              {avatarSrc ? <AvatarImage src={avatarSrc} alt="" /> : null}
              <AvatarFallback className="bg-[oklch(0.72_0.14_95)] text-[12px] font-semibold text-background">
                {initials}
              </AvatarFallback>
            </Avatar>
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-black/0 opacity-0 transition-colors duration-150 group-hover:bg-black/45 group-hover:opacity-100">
              <Upload className="h-4 w-4 text-white" />
            </span>
          </button>
        </FormRow>
        <FormRow label="Email">
          <span className="text-[13px] text-muted-foreground">{email}</span>
        </FormRow>
        <FormRow label="Full name">
          <Input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            onBlur={() => void saveName()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                (e.target as HTMLInputElement).blur();
              }
            }}
            className="w-full"
          />
        </FormRow>
      </FormCard>
    </div>
  );
}

function VoiceProfilesContainer() {
  const { currentSlug } = useCurrentWorkspace();
  const spokenMemoryApi = useMemo(
    () => createSpokenMemoryApi({ workspaceSlug: currentSlug }),
    [currentSlug],
  );
  const {
    data: myProfileData,
    error: myProfileError,
    isLoading: myProfileLoading,
    refetch: refetchMyProfile,
  } = useMyVoiceProfileQuery(currentSlug);
  const [voiceTestModal, setVoiceTestModal] = useState<"start" | "redo" | null>(
    null,
  );
  const retrySetup = useRetrySpeakerSetupMutation(currentSlug);
  const refreshMyProfile = useCallback(async () => {
    await refetchMyProfile();
  }, [refetchMyProfile]);

  return (
    <div className="mx-auto max-w-3xl">
      <SettingsHeader
        title="Voice profiles"
        description="Record a short voice test in your browser so transcripts attribute speech to you."
      />
      <div className="space-y-6">
        <VoiceProfileSettingsContent
          api={spokenMemoryApi}
          profile={myProfileData?.voice_profile ?? null}
          loading={myProfileLoading}
          error={
            retrySetup.error
              ? String(retrySetup.error)
              : myProfileError instanceof Error
                ? myProfileError.message
                : myProfileError
                  ? String(myProfileError)
                  : null
          }
          onStartVoiceTest={(mode) => setVoiceTestModal(mode)}
          onRetrySetup={() => retrySetup.mutate()}
          retrySetupPending={retrySetup.isPending}
        />
        <VoiceTestModal
          api={spokenMemoryApi}
          workspaceSlug={currentSlug}
          open={voiceTestModal != null}
          mode={voiceTestModal ?? "start"}
          onClose={() => setVoiceTestModal(null)}
          onCompleted={refreshMyProfile}
        />
      </div>
    </div>
  );
}

export function VoiceProfileSettingsContent({
  api,
  profile,
  loading,
  error,
  onStartVoiceTest,
  onRetrySetup,
  retrySetupPending,
}: {
  api: Pick<SpokenMemoryApi, "voiceProfileSampleAudioUrl">;
  profile: VoiceProfile | null;
  loading?: boolean;
  error?: string | null;
  onStartVoiceTest: (mode: "start" | "redo") => void;
  onRetrySetup?: () => void;
  retrySetupPending?: boolean;
}) {
  const audioSrc = profile?.has_sample_audio
    ? api.voiceProfileSampleAudioUrl(profile.id)
    : null;
  const readiness = profile ? voiceProfileReadiness(profile) : null;
  // Hosted setup failed on a good sample: the one next action is retrying
  // setup, not re-recording (plan 027 user-facing states).
  const setupRetryable =
    profile?.hosted_voiceprint_status === "failed" &&
    profile?.has_hosted_voiceprint !== true;

  return (
    <div className="space-y-6">
      {error ? <InlineMessage variant="error">{error}</InlineMessage> : null}
      <FormCard>
        <FormStackedRow
          label="Your voice profile"
          hint="A 30-second read-aloud recording from your browser microphone. Redo it any time your setup changes."
        >
          {loading ? (
            <div className="flex items-center gap-2 py-3 text-[13px] text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading voice profile
            </div>
          ) : profile ? (
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
                <div className="min-w-0">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <div className="truncate text-[14px] font-medium text-foreground">
                      {profile.display_name}
                    </div>
                    {readiness ? (
                      <VoiceProfileReadinessBadge readiness={readiness} />
                    ) : null}
                  </div>
                  <div className="mt-1 text-[12px] text-muted-foreground">
                    {profile.enrollment_source} · {profile.status} · enrolled{" "}
                    {formatSettingsDate(profile.created_at)}
                  </div>
                </div>
                {setupRetryable ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={retrySetupPending}
                    onClick={() => onRetrySetup?.()}
                  >
                    {retrySetupPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" />
                    )}
                    Try setup again
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onStartVoiceTest("redo")}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Redo Voice Test
                  </Button>
                )}
              </div>

              {readiness && readiness.tone !== "ready" ? (
                <InlineMessage variant="info">{readiness.detail}</InlineMessage>
              ) : null}

              <div className="grid gap-2 sm:grid-cols-3">
                <ProfileMetric
                  label="Attribution"
                  value={readiness?.label ?? "n/a"}
                />
                <ProfileMetric
                  label="Sample"
                  value={voiceProfileSampleLabel(profile)}
                />
                <ProfileMetric
                  label="Speech"
                  value={formatDuration(profile.total_speech_ms)}
                />
              </div>

              {audioSrc ? (
                <audio
                  controls
                  preload="metadata"
                  crossOrigin="use-credentials"
                  src={audioSrc}
                  className="w-full"
                />
              ) : null}
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-[13px] text-muted-foreground">
                No voice test recorded.
              </div>
              <Button
                type="button"
                size="sm"
                onClick={() => onStartVoiceTest("start")}
              >
                <Mic className="h-3.5 w-3.5" />
                Start Voice Test
              </Button>
            </div>
          )}
        </FormStackedRow>
      </FormCard>
    </div>
  );
}

type VoiceProfileReadiness = {
  label: string;
  detail: string;
  tone: "ready" | "warning";
};

function voiceProfileReadiness(profile: VoiceProfile): VoiceProfileReadiness {
  if (
    profile.attribution_ready === true ||
    profile.attribution_status === "ready"
  ) {
    return {
      label: "Ready",
      detail: "Ready for speaker attribution.",
      tone: "ready",
    };
  }
  if (profile.attribution_status === "needs_redo") {
    return {
      label: "Needs redo",
      detail: enrollmentRedoCopy(
        profile.attribution_status_reason ?? profile.sample_status_reason,
      ),
      tone: "warning",
    };
  }
  if (profile.attribution_status === "missing_hosted_voiceprint") {
    if (profile.hosted_voiceprint_status === "failed") {
      return {
        label: "Needs redo",
        detail: "Speaker setup failed. Try setup again.",
        tone: "warning",
      };
    }
    return {
      label: "Finishing setup",
      detail: "Almost there — speaker setup is finishing in the background.",
      tone: "warning",
    };
  }
  if (profile.sample_status === "needs_redo") {
    return {
      label: "Needs redo",
      detail: enrollmentRedoCopy(profile.sample_status_reason),
      tone: "warning",
    };
  }
  return {
    label: "Not ready",
    detail: "Speaker attribution readiness is unknown.",
    tone: "warning",
  };
}

function VoiceProfileReadinessBadge({
  readiness,
}: {
  readiness: VoiceProfileReadiness;
}) {
  const Icon = readiness.tone === "ready" ? CheckCircle2 : CircleAlert;
  const className =
    readiness.tone === "ready"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
      : "border-amber-500/30 bg-amber-500/10 text-amber-200";
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${className}`}
    >
      <Icon className="h-3 w-3" />
      {readiness.label}
    </span>
  );
}

function voiceProfileSampleLabel(profile: VoiceProfile): string {
  if (profile.sample_status === "needs_redo") return "Needs redo";
  if (profile.sample_status === "accepted") return "Accepted";
  return profile.sample_status ?? profile.status;
}

function ProfileMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <div className="text-[11px] font-medium text-muted-foreground uppercase">
        {label}
      </div>
      <div className="mt-1 truncate text-[13px] text-foreground">{value}</div>
    </div>
  );
}

function EmptyProcessingRow({ text }: { text: string }) {
  return (
    <div className="border-t border-border py-3 text-[13px] text-muted-foreground">
      {text}
    </div>
  );
}

function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return "0s";
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

function roleLabel(role: WorkspaceRoleSlug): string {
  if (role === "admin") return "Admin";
  return "Member";
}

function formatSettingsDate(value: string | number): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "recently";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
